const LOCALE = 'en-US';
const formatterCache = new Map<number, Intl.NumberFormat>();

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const easeOutQuint = (progress: number) => 1 - Math.pow(1 - progress, 5);

export const easeOutQuintSpeed = (progress: number) =>
  5 * Math.pow(1 - progress, 4);

export const getDecimalPlaces = (value: number) => {
  if (!Number.isFinite(value)) return 0;

  const normalized = Number(value.toPrecision(12));
  if (Number.isInteger(normalized)) return 0;

  const match = String(normalized).match(/\.(\d+)/);
  return match?.[1]?.length ?? 0;
};

export const getFormatter = (decimals: number) => {
  const safeDecimals = clamp(decimals, 0, 8);
  const cached = formatterCache.get(safeDecimals);

  if (cached) return cached;

  const formatter = new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: safeDecimals,
    maximumFractionDigits: safeDecimals,
  });
  formatterCache.set(safeDecimals, formatter);
  return formatter;
};

export interface NumberLanes {
  body: string[];
  mark: string;
}

export const splitNumberLanes = (
  formatter: Intl.NumberFormat,
  value: number
): NumberLanes => {
  const body: string[] = [];
  let mark = ',';
  let current = '';
  let fraction = '';

  for (const part of formatter.formatToParts(value)) {
    if (part.type === 'group') {
      mark = part.value;
      body.push(current);
      current = '';
      continue;
    }

    if (part.type === 'integer') {
      current += part.value;
      continue;
    }

    if (part.type === 'minusSign' || part.type === 'plusSign') {
      current = part.value + current;
      continue;
    }

    if (part.type === 'decimal' || part.type === 'fraction') {
      fraction += part.value;
    }
  }

  body.push(current + fraction);
  return { body, mark };
};

export const getLaneCount = (
  formatter: Intl.NumberFormat,
  ...values: number[]
) =>
  Math.max(
    1,
    ...values.map(
      (value) => splitNumberLanes(formatter, Math.abs(value || 1)).body.length
    )
  );

export const setBlur = (
  blurNode: SVGFEGaussianBlurElement | null,
  amount: number,
  direction: 'horizontal' | 'vertical' = 'horizontal'
) => {
  if (!blurNode) return;
  const blurStr = amount.toFixed(2);
  const stdDev = direction === 'vertical' ? `0,${blurStr}` : `${blurStr},0`;
  blurNode.setAttribute('stdDeviation', stdDev);
};
